import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
    Alert,
    Button,
    Card,
    Flex,
    Heading,
    Text,
    View,
    useTheme,
    Divider
} from '@aws-amplify/ui-react';
import { FetchError } from '../utils/fetchWithRetry';

interface ErrorDisplayProps {
    error: FetchError | null;
    onRetry?: () => void;
    onSignOut?: () => void;
    showDetails?: boolean;
}

/**
 * エラー表示コンポーネント
 * 
 * ユーザーがスクリーンショットで報告できるよう、
 * エラーコード、タイムスタンプ、詳細情報を明確に表示
 */
export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
    error,
    onRetry,
    onSignOut,
    showDetails = true
}) => {
    const { tokens } = useTheme();

    if (!error) {
        return null;
    }

    // エラーコードの短縮版を生成（スクリーンショット用）
    const generateErrorCode = (error: FetchError): string => {
        const timestamp = new Date(error.timestamp).getTime();
        const code = error.code || 'UNKNOWN';
        return `ERR-${code}-${timestamp.toString().slice(-8)}`;
    };

    const errorCode = generateErrorCode(error);
    const isAuthError = !error.isRetryable;

    // エラーメッセージの日本語化
    const getErrorMessage = (error: FetchError): string => {
        if (error.code === 'TIMEOUT') {
            return 'ネットワークがタイムアウトしました。接続を確認して再度お試しください。';
        }
        if (error.code === 'NotAuthorizedException') {
            return 'セッションの有効期限が切れました。再度ログインしてください。';
        }
        if (error.isRetryable) {
            return 'ネットワークエラーが発生しました。しばらくしてから再度お試しください。';
        }
        return error.message || '予期しないエラーが発生しました。';
    };

    const errorMessage = getErrorMessage(error);

    return (
        <View
            padding={tokens.space.large}
            maxWidth="600px"
            margin="0 auto"
        >
            <Card variation="outlined">
                <Flex direction="column" gap={tokens.space.medium}>
                    {/* エラーアイコンとタイトル */}
                    <Flex direction="column" alignItems="center" gap={tokens.space.small}>
                        <div style={{ fontSize: '48px' }}>⚠️</div>
                        <Heading level={4}>
                            {isAuthError ? 'ログインエラー' : '接続エラー'}
                        </Heading>
                    </Flex>

                    <Divider />

                    {/* エラーメッセージ */}
                    <Alert
                        variation={isAuthError ? 'error' : 'warning'}
                        isDismissible={false}
                        hasIcon={true}
                    >
                        {errorMessage}
                    </Alert>

                    {/* エラーコード（スクリーンショット用） */}
                    <Card
                        variation="outlined"
                        backgroundColor={tokens.colors.background.secondary}
                        padding={tokens.space.medium}
                    >
                        <Flex direction="column" gap={tokens.space.xs}>
                            <Text fontSize={tokens.fontSizes.xs} color={tokens.colors.font.secondary}>
                                エラーコード（サポートへの問い合わせ時にご利用ください）
                            </Text>
                            <Text
                                fontSize={tokens.fontSizes.xl}
                                fontWeight={tokens.fontWeights.bold}
                                fontFamily="monospace"
                                style={{
                                    userSelect: 'all',
                                    backgroundColor: tokens.colors.background.primary.value,
                                    padding: tokens.space.xs.value,
                                    borderRadius: '4px',
                                    border: `1px solid ${tokens.colors.border.primary.value}`
                                }}
                            >
                                {errorCode}
                            </Text>
                        </Flex>
                    </Card>

                    {/* 詳細情報（デバッグ用） */}
                    {showDetails && (
                        <Card
                            variation="outlined"
                            backgroundColor={tokens.colors.background.secondary}
                            padding={tokens.space.small}
                        >
                            <Flex direction="column" gap={tokens.space.xs}>
                                <Text fontSize={tokens.fontSizes.xs} fontWeight={tokens.fontWeights.bold}>
                                    詳細情報
                                </Text>
                                <Text fontSize={tokens.fontSizes.xs} fontFamily="monospace">
                                    エラー種別: {error.code}
                                </Text>
                                <Text fontSize={tokens.fontSizes.xs} fontFamily="monospace">
                                    発生日時: {new Date(error.timestamp).toLocaleString('ja-JP')}
                                </Text>
                                {error.details?.attempt && (
                                    <Text fontSize={tokens.fontSizes.xs} fontFamily="monospace">
                                        試行回数: {error.details.attempt}
                                    </Text>
                                )}
                                <Text fontSize={tokens.fontSizes.xs} fontFamily="monospace">
                                    リトライ可否: {error.isRetryable ? '可能' : '不可'}
                                </Text>
                            </Flex>
                        </Card>
                    )}

                    {/* アクションボタン */}
                    <Flex justifyContent="center" gap={tokens.space.medium} marginTop={tokens.space.medium}>
                        {error.isRetryable && onRetry && (
                            <Button onClick={onRetry} variation="primary" size="large">
                                <FormattedMessage id="retryButton" defaultMessage="再試行" />
                            </Button>
                        )}
                        {isAuthError && onSignOut && (
                            <Button onClick={onSignOut} variation="primary" size="large">
                                <FormattedMessage id="signOutButton" defaultMessage="ログイン画面へ" />
                            </Button>
                        )}
                    </Flex>

                    {/* サポート案内 */}
                    <Card backgroundColor={tokens.colors.background.info} padding={tokens.space.small}>
                        <Text fontSize={tokens.fontSizes.xs} textAlign="center">
                            💡 問題が解決しない場合は、上記のエラーコードをスクリーンショットで撮影し、サポートまでお問い合わせください。
                        </Text>
                    </Card>
                </Flex>
            </Card>
        </View>
    );
};

